import React, {
  createContext,
  useContext,
  useState,
  useCallback,
  useEffect
} from "react"
import { SelectAllState } from "../types"

interface SelectAllContextValue {
  selectAllState: SelectAllState | null
  setSelectAllState: (state: SelectAllState | null) => void
  clearSelectAll: () => void
  isSelectAllActive: boolean
}

const SelectAllContext = createContext<SelectAllContextValue | undefined>(
  undefined
)

export const useSelectAllContext = () => {
  const context = useContext(SelectAllContext)
  if (!context) {
    throw new Error("useSelectAllContext must be used within SelectAllProvider")
  }
  return context
}

interface SelectAllProviderProps {
  children: React.ReactNode
}

export const SelectAllProvider: React.FC<SelectAllProviderProps> = ({
  children
}) => {
  const [selectAllState, setSelectAllState] = useState<SelectAllState | null>(
    null
  )

  const clearSelectAll = useCallback(() => {
    setSelectAllState(null)
  }, [])

  const isSelectAllActive = selectAllState?.isSelectAllActive ?? false

  // Clear select all state when filters change
  useEffect(() => {
    const handleFiltersChange = () => {
      clearSelectAll()
    }

    window.addEventListener("filtersChanged", handleFiltersChange)
    return () => {
      window.removeEventListener("filtersChanged", handleFiltersChange)
    }
  }, [clearSelectAll])

  // Clear select all state after bulk action success
  useEffect(() => {
    const handleBulkActionSuccess = () => {
      clearSelectAll()
    }

    window.addEventListener("bulkActionSuccess", handleBulkActionSuccess)
    return () => {
      window.removeEventListener("bulkActionSuccess", handleBulkActionSuccess)
    }
  }, [clearSelectAll])

  const value: SelectAllContextValue = {
    selectAllState,
    setSelectAllState,
    clearSelectAll,
    isSelectAllActive
  }

  return (
    <SelectAllContext.Provider value={value}>
      {children}
    </SelectAllContext.Provider>
  )
}
